import { Fragment, useCallback, useId, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CollectionImage, CurrencyIcon } from '~/components/collections';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage } from '~/rage';
import { WalletType, walletTypeCurrency } from '~/shared';
import {
	setActiveBundle,
	setActiveCase,
	setShopKitOpen,
	setShopOpenCaseVisible,
	setTabIndex,
	useLayoutDispatch,
} from '../../redux';
import { formatTime } from '../../utils';
import { CaseType, ICaseItem } from './CaseItem.model';
import {
	StyledBackgroundSvg,
	StyledBg,
	StyledBottomContainer,
	StyledCaseImageContainer,
	StyledCaseShadowItem,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledKitImage,
	StyledMoreBlock,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledTag,
	StyledTagsContainer,
	StyledTimerContainer,
	StyledTopContainer,
} from './CaseItem.styled';

const CaseItem = ({
	id,
	type,
	name,
	discount,
	price,
	oldPrice,
	color,
	image,
	isMultiple,
	span,
	wallet,
	tags,
	dateDiscountEnd,
	dateEnd,
}: ICaseItem) => {
	const dispatch = useLayoutDispatch();
	const gradientId = useId();
	const [discountTime, setDiscountTime] = useState('');
	const [endTime, setEndTime] = useState('');

	const currencyIcon = useWalletIcon(
		walletTypeCurrency[wallet || WalletType.Cash],
		true,
	);

	const tagsConfig = useMemo(() => {
		return tags?.map((tag) => rage.cases.getCaseTagById(tag));
	}, [tags]);

	const handleClick = useCallback(async () => {
		if (type === CaseType.Default) {
			const selectedCase = rage.cases.getLootboxById(id);
			const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
				selectedCase as any;
			dispatch(setActiveCase(caseWithoutConfig));
			dispatch(setTabIndex(1));
			dispatch(setShopOpenCaseVisible(true));
		} else {
			const selectedBundle = rage.cases.getBundleById(id);
			const { getClientConfig: _getClientConfig, ...bundleWithoutConfig } =
				selectedBundle as any;
			dispatch(setActiveBundle(bundleWithoutConfig));
			dispatch(setTabIndex(1));
			dispatch(setShopKitOpen(true));
		}
	}, [type, id]);

	const countTime = useCallback(() => {
		if (dateDiscountEnd) {
			const { time, unit } = formatTime(dateDiscountEnd);
			if (!unit) {
				setDiscountTime(time);
			} else {
				setDiscountTime(`${time} ${unit}`);
			}
		}

		if (dateEnd) {
			const { time, unit } = formatTime(dateEnd);
			if (!unit) {
				setEndTime(time);
			} else {
				setEndTime(`${time} ${unit}`);
			}
		}
	}, [dateDiscountEnd, dateEnd]);

	useEffectOnce(() => {
		countTime();
		const interval = setInterval(countTime, 1000);
		return () => clearInterval(interval);
	});

	return (
		<StyledContainer
			onClick={() => {
				handleClick();
				playSound(Sound.ClickButton);
			}}
			$type={type}
			onMouseEnter={() => {
				playSound(Sound.LinkCursor);
			}}
			$span={span}
		>
			{type === CaseType.Default && <StyledBg />}
			{color && (
				<StyledBackgroundSvg>
					<ellipse
						opacity="0.1"
						cx="165"
						cy="135"
						rx="165"
						ry="135"
						fill={`url(#${gradientId})`}
					/>
					<defs>
						<radialGradient
							id={gradientId}
							cx="0"
							cy="0"
							r="1"
							gradientUnits="userSpaceOnUse"
							gradientTransform="translate(165 135) rotate(90) scale(135 165)"
						>
							<stop stopColor={color} />
							<stop offset="1" stopColor={color} stopOpacity="0" />
						</radialGradient>
					</defs>
				</StyledBackgroundSvg>
			)}
			{type === CaseType.Default && (
				<StyledCaseImageContainer>
					<CollectionImage collection={'cases'} id={image} size={180} />
				</StyledCaseImageContainer>
			)}
			{type === CaseType.Kit && (
				<StyledKitImage>
					<CollectionImage collection={'kits'} id={image} size={240} />
				</StyledKitImage>
			)}
			{type === CaseType.Kit && <StyledShadow />}
			{type === CaseType.Default && (
				<Fragment>
					<StyledCaseShadowItem />
					<StyledCaseShadowItem />
				</Fragment>
			)}
			<StyledTopContainer $isMultiple={isMultiple}>
				<StyledTagsContainer>
					{discountTime && discount ? (
						<StyledDiscountTimerContainer>
							<Text>{discount * 100}%</Text>
							<div className={'divider'} />
							<Text>{discountTime}</Text>
						</StyledDiscountTimerContainer>
					) : (
						<>
							{endTime && (
								<StyledTimerContainer>
									<Text>{endTime}</Text>
								</StyledTimerContainer>
							)}
							{discount ? (
								<StyledDiscountContainer>
									<Text>{discount * 100}%</Text>
								</StyledDiscountContainer>
							) : null}
						</>
					)}
					{tagsConfig?.map((tag) => (
						<StyledTag
							$bgColorNormal={tag?.bgColorNormal ?? ''}
							$textColorNormal={tag?.textColorNormal ?? ''}
							key={tag?.id}
						>
							<Text>{tag?.text}</Text>
						</StyledTag>
					))}
				</StyledTagsContainer>
			</StyledTopContainer>
			<StyledBottomContainer>
				<Text>{name}</Text>
				<StyledPriceContainer>
					<StyledPrice $isDonate={wallet === WalletType.Donate}>
						<CurrencyIcon
							icon={currencyIcon}
							size="md"
							color={wallet === WalletType.Donate ? '#FFBB00' : '#ffffff'}
						/>
						<Text>{price.toLocaleString('ru-RU')}</Text>
					</StyledPrice>
					{oldPrice && (
						<StyledOldPrice>
							<CurrencyIcon icon={currencyIcon} size={10} />
							<Text>{oldPrice.toLocaleString('ru-RU')}</Text>
							<div className={'line'} />
						</StyledOldPrice>
					)}
				</StyledPriceContainer>
				<StyledMoreBlock>
					<Text>Подробнее</Text>
					<img src={arrowRight} />
				</StyledMoreBlock>
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { CaseItem };
